import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  )
}

/**
 * Requires a valid session (authenticated user).
 * Redirects to /login if not authenticated.
 * Redirects to /change-password if force_password_change is set.
 */
export function AuthRequired({ children }) {
  const { isAuthenticated, forcePasswordChange, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  return children
}

/**
 * Requires only a valid session — no redirect for force_password_change.
 * Use ONLY for the /change-password route itself to avoid infinite loops.
 */
export function AuthOnlyRequired({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return children
}

/**
 * Requires a valid session AND an active tenant selected.
 * Redirects to /select-tenant if authenticated but no tenant.
 */
export function TenantRequired({ children }) {
  const { isAuthenticated, hasTenant, forcePasswordChange, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  return children
}

/**
 * Requires a valid session, active tenant, AND onboarding completed.
 * Also enforces MFA: redirects to /mfa-challenge or /mfa-setup if MFA is pending.
 */
export function AppRequired({ children }) {
  const { isAuthenticated, hasTenant, onboardingCompleted, forcePasswordChange, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated && !mfaPending) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  return children
}

/**
 * Requires a valid session, active tenant, and admin-level permissions.
 * Allows wildcard '*' or any of the admin-facing permissions
 * (members, roles, settings, policies management).
 *
 * Includes policy:read / policy:configure so a tenant policy admin
 * (no member / role / settings grant) can still reach the admin
 * landing and the /admin/policies list, and cost_management:* so a
 * billing administrator can reach /admin/currency. The rule is that
 * every capability guarding an admin card appears here: a card the
 * landing would show to somebody this guard turns away is a page with
 * no route to it.
 */
export function AdminRequired({ children }) {
  const { isAuthenticated, hasTenant, hasAnyCapability, forcePasswordChange, onboardingCompleted, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated && !mfaPending) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  const canAccessAdmin = hasAnyCapability('*')
    || hasAnyCapability('members:read')
    || hasAnyCapability('roles:read')
    || hasAnyCapability('settings:read')
    || hasAnyCapability('policy:read')
    || hasAnyCapability('policy:configure')
    || hasAnyCapability('cost_management:read')
    || hasAnyCapability('cost_management:manage')
  if (!canAccessAdmin) return <Navigate to="/home" replace />
  return children
}

/**
 * Requires a valid session + active tenant + onboarding completion AND
 * an active tenant feature flag.
 *
 * Used to hide feature-gated routes (e.g. ABAC policy admin pages)
 * when the tenant has the feature disabled. Mirrors the backend
 * `require_feature(...)` dependency so the client-side guard fails
 * closed: a user navigating to a gated URL with the feature off lands
 * on /home rather than a blank page (the API would 403 anyway,
 * but the UX is better when the redirect happens client-side).
 */
export function FeatureRequired({ feature, children }) {
  const { isAuthenticated, hasTenant, isFeatureEnabled, forcePasswordChange, onboardingCompleted, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated && !mfaPending) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  if (feature && !isFeatureEnabled(feature)) return <Navigate to="/home" replace />
  return children
}

/**
 * Requires a valid session + active tenant + onboarding completion
 * (i.e. everything AppRequired enforces) AND a specific permission.
 *
 * Designed to be a minimal addition to the existing guard family:
 * pages opt in via `<PermissionRequired permission="...">…</PermissionRequired>`.
 * Render-time permission failures redirect to /home so the user
 * lands somewhere meaningful instead of a blank page.
 */
export function PermissionRequired({ permission, children }) {
  const { isAuthenticated, hasTenant, hasAnyCapability, forcePasswordChange, onboardingCompleted, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated && !mfaPending) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  if (permission && !hasAnyCapability(permission)) return <Navigate to="/home" replace />
  return children
}

export function AnyPermissionRequired({ permissions, children }) {
  const { isAuthenticated, hasTenant, hasAnyCapability, forcePasswordChange, onboardingCompleted, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) return <LoadingSpinner />
  if (!isAuthenticated && !mfaPending) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  if (permissions?.length && !permissions.some((p) => hasAnyCapability(p))) {
    return <Navigate to="/home" replace />
  }
  return children
}

// Default export kept for backward compatibility with any remaining imports
export default AuthRequired
